import { ImportPreview } from "@/components/billing/ImportPreview";
import { JobberChecklist } from "@/components/billing/JobberChecklist";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileUp, Mail } from "lucide-react";
import { ApiError, api, getToken } from "@/lib/api";
import { Btn, Card } from "@/components/PortalPage";

const BASE = process.env.REACT_APP_BACKEND_URL;
const LABELS = { gmail: "ALR email" };

export const startOAuth = async (provider) => {
  try {
    const r = await api(`/billing/oauth/${provider}/start`, { method: "POST", body: { origin_url: BASE } });
    window.location.href = r.url;
  } catch (e) { toast.error(e.message); }
};

const useConnections = () => {
  const [c, setC] = useState(null);
  const [params] = useSearchParams();
  const load = () => api("/billing/connections").then(setC).catch(() => setC({}));
  useEffect(() => {
    load();
    const k = params.get("connect");
    if (k && LABELS[k]) toast.success(`${LABELS[k]} connected`);
    else if (k === "wrong_domain") toast.error("Only your ALR email (ending in @alrclean.com) can be connected. Please sign in with your ALR Google account.", { duration: 9000 });
    else if (k) toast.error(k === "expired" ? "That connection link expired. Please try again." : "The connection wasn't completed.");
  }, [params]);
  return [c, load];
};

const ImportResult = ({ imp }) => {
  if (!imp) return null;
  const n = (c, w) => `${c} ${c === 1 ? w.replace(/s$/, "") : w}`;
  const parts = [n(imp.customers || 0, "new customers"), ...["leads", "quotes", "jobs", "invoices"].filter((k) => imp[k]).map((k) => n(imp[k], k))];
  return (
    <div className="space-y-1 rounded-lg border border-[#2a2a2a] bg-[#141414] p-3 text-xs text-[#ccc]" data-testid="jobber-import-status">
      <p className="font-bold text-white">Last import {imp.finished_at ? new Date(imp.finished_at).toLocaleString() : ""}</p>
      <p>Added {parts.join(", ")}{imp.updated ? `, updated ${imp.updated}` : ""}{imp.kept ? `, kept ${imp.kept} you've already changed here` : ""}{imp.skipped ? `, skipped ${imp.skipped} rows with no customer` : ""}.</p>
      {imp.next_numbers && Object.keys(imp.next_numbers).length > 0 && <p data-testid="jobber-next-numbers">Your numbers continue at: {Object.entries(imp.next_numbers).map(([k, v]) => `${k} #${v}`).join(", ")}</p>}
      {(imp.files || []).map((f) => <p key={f.name} className={f.kind ? "" : "text-[#FFB800]"}>{f.name}: {f.kind ? `${f.kind} (${f.rows} row${f.rows === 1 ? "" : "s"})` : "not recognised, check it's a Jobber export"}</p>)}
    </div>
  );
};

export const ConnectionCards = () => {
  const [c, reload] = useConnections();
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [check, setCheck] = useState(null);
  if (!c) return null;
  const disconnect = async () => { await api("/billing/mail/disconnect", { method: "POST" }); reload(); };
  const send = async (list, dry) => {
    setBusy(true);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      const res = await fetch(`${BASE}/api/billing/import/csv?dry_run=${dry}`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const j = await res.json();
      if (!res.ok) throw new ApiError(typeof j.detail === "string" ? j.detail : "Import failed", res.status);
      const full = await api(`/billing/import/batches/${j.batch_id}`);
      setPreview({ ...j, rows: full.rows });
      toast.success(dry ? "Preview ready. Nothing has been saved yet." : "New records added. Existing records were not changed.");
      if (!dry) reload();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const upload = async (e) => {
    const list = [...(e.target.files || [])];
    e.target.value = "";
    if (!list.length) return;
    setFiles(list);
    setPreview(null);
    setBusy(true);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      const res = await fetch(`${BASE}/api/billing/import/check`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const j = await res.json();
      if (!res.ok) throw new ApiError(typeof j.detail === "string" ? j.detail : "Check failed", res.status);
      setCheck(j);
      setBusy(false);
      if (j.ready) send(list, true);
      else toast.error("Import blocked: fix the files marked in red and upload all 4 again.");
    } catch (err) { toast.error(err.message); setBusy(false); }
  };
  return (
    <>
      <Card className="space-y-3" data-testid="billing-mail-card">
        <p className="text-xs font-bold tracking-[2px] text-[var(--accent)]">4 · EMAIL FROM YOUR OWN INBOX</p>
        <p className="text-sm text-[#ccc]">Quotes and invoices go out from your ALR email (the <b className="text-white">@alrclean.com</b> address ALR gave you), and replies come straight back to your inbox. Personal Gmail and other email addresses can't be connected.</p>
        {c.mail_provider ? (
          <div className="flex items-center gap-3 text-sm" data-testid="mail-connected-info"><Mail size={16} className="text-[var(--accent)]" /><span className="flex-1">{LABELS[c.mail_provider]} · {c.mail_email}</span><button onClick={disconnect} className="text-xs font-bold text-[#ff8080]" data-testid="mail-disconnect-btn">Disconnect</button></div>
        ) : c.gmail_available ? (
          <div className="space-y-1.5">
            <Btn variant="outline" onClick={() => startOAuth("gmail")} data-testid="connect-gmail-btn">CONNECT MY ALR EMAIL</Btn>
            <p className="text-[11px] text-[#888]">Google will ask you to sign in. Choose your @alrclean.com account and tap Allow.</p>
          </div>
        ) : <p className="text-xs text-[#888]" data-testid="mail-coming-soon">ALR is finishing the email setup. Until then, copy the customer link from any quote or invoice.</p>}
      </Card>
      <Card className="space-y-3" data-testid="billing-jobber-card">
        <p className="text-xs font-bold tracking-[2px] text-[var(--accent)]">5 · BRING OVER YOUR JOBBER DATA</p>
        <p className="text-sm text-[#ccc]">Download these 4 exports from Jobber on a computer (an admin login is needed), then upload them here together. Customers, quotes, jobs and invoices come over with their line items, totals, tax and balances. Your quote, job and invoice numbers continue from where Jobber left off.</p>
        <JobberChecklist check={check} />
        <p className="text-xs text-[#ccc]" data-testid="jobber-step-upload"><b className="text-white">Then upload all 4 together.</b> Jobber emails each export (big ones come as "1 of 2" files). Download them, tap the button and select every file at once. The import only starts when all 4 are ticked.</p>
        <label className={`flex w-fit cursor-pointer items-center gap-1.5 rounded-lg bg-[var(--accent)] px-5 py-3 text-xs font-extrabold text-black ${busy ? "opacity-50" : ""}`} data-testid="csv-upload-label"><FileUp size={14} />{busy ? "CHECKING…" : "UPLOAD JOBBER EXPORTS (PREVIEW FIRST)"}<input type="file" multiple accept=".csv,text/csv" className="hidden" disabled={busy} onChange={upload} data-testid="csv-upload-input" /></label>
        <ImportResult imp={c.jobber_import} />
        <p className="text-[11px] text-[#888]">Every upload is checked first and nothing is saved until you confirm. If an export has no line items, the record is marked incomplete. Uploading again only adds new records: existing ones are never changed, and differences are listed for review. Royalty only applies to imported balances your customers pay after the import. Stuck? Ask the ALR Guide.</p>
      </Card>
      {preview && <ImportPreview res={preview} busy={busy} onApply={() => send(files, false)} />}
    </>
  );
};
